import { h, Fragment } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import Image from '../utils/image';
import Icon from '../utils/icon';
import { useProduct } from './product-context';
import './kit/product-selector.css';
import Storefront from 'utils/storefront';
import { productsByTag, productByHandle } from 'utils/storefront/queries';
import { formatMoney } from 'utils/format-money';
import Reviews from './yotpo-reviews';

const ProductDropdown = ({
  products,
  selectedProduct,
  selectedVariant,
  setSelectedProduct,
  selectedLengthTag,
  setSelectedVariant,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (selectedProduct.lengthTag !== selectedLengthTag) {
      const oldProductSize = selectedVariant.node.selectedOptions.find(
        (option) => option.name === 'Size',
      ).value;
      const oldProductColor = selectedVariant.node.selectedOptions.find(
        (option) => option.name === 'Color',
      ).value;
      const newProduct = products.find((product) => {
        const colorVariant = product.variants.edges.find((variant) => {
          const color = variant.node.selectedOptions.find(
            (option) => option.name === 'Color',
          ).value;
          return (
            color === oldProductColor && product.lengthTag === selectedLengthTag
          );
        });
        return colorVariant;
      });

      const newProductSize = newProduct.variants.edges.find((variant) => {
        const size = variant.node.selectedOptions.find(
          (option) => option.name === 'Size',
        ).value;
        return size === oldProductSize;
      });

      setSelectedProduct(newProduct);
      setSelectedVariant(newProductSize);
    }
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownRef, selectedLengthTag]);

  const handleClick = (product) => {
    const oldProductSize = selectedVariant.node.selectedOptions.find(
      (option) => option.name === 'Size',
    ).value;

    setSelectedProduct(product);
    setSelectedVariant(
      product.variants.edges.find((variant) => {
        const size = variant.node.selectedOptions.find(
          (option) => option.name === 'Size',
        ).value;
        return size === oldProductSize;
      }),
    );
    setIsOpen(false);
  };

  return (
    <div className="w-full relative mb-[8px]" ref={dropdownRef}>
      <div
        className="flex flex-row justify-between pl-[10px] pr-[10px] w-full border-1 border-grey-400 rounded-[3px] text-h6 text-grey-600 items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-grey-600 text-h6 py-[6px]">
          {selectedProduct.title.split(':')[1]}
        </span>
        <Icon id="chevron-down" className="h-[10px] w-[10px]" />
      </div>
      <div
        className={`max-h-[200px] overflow-y-scroll bg-white border-1 border-grey-400 rounded-b-[3px] border-t-0 absolute w-full z-1 ${
          isOpen ? 'block' : 'hidden'
        }`}
      >
        {products.map(
          (product) =>
            product.lengthTag === selectedLengthTag && (
              <div
                onClick={() => handleClick(product)}
                className="pl-[10px] cursor-pointer flex flex-row items-center pt-[9px] pb-[9px] hover:bg-tan-100"
              >
                <p className="text-h6 text-grey-600">
                  {product.title.split(':')[1]}
                </p>
              </div>
            ),
        )}
      </div>
    </div>
  );
};

const VariantDropdown = ({ variants, selectedVariant, setSelectedVariant }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownRef]);

  const handleClick = (variant) => {
    setSelectedVariant(variant);
    setIsOpen(false);
  };

  return (
    <div className="w-full relative mb-[8px]" ref={dropdownRef}>
      <div
        className="flex flex-row justify-between pl-[10px] pr-[10px] w-full border-1 border-grey-400 rounded-[3px] text-h6 text-grey-600 items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-grey-600 text-h6 py-[6px]">
          Size: {selectedVariant.node.title.split('/')[0]}
        </span>
        <Icon id="chevron-down" className="h-[10px] w-[10px]" />
      </div>
      <div
        className={`max-h-[200px] overflow-y-scroll bg-white border-1 border-grey-400 rounded-b-[3px] border-t-0 absolute w-full z-1 ${
          isOpen ? 'block' : 'hidden'
        }`}
      >
        {variants.map((variant) => (
          <div
            onClick={() => handleClick(variant)}
            className="pl-[10px] cursor-pointer flex flex-row items-center pt-[9px] pb-[9px] hover:bg-tan-100"
          >
            <p className="text-h6 text-grey-600">
              {variant.node.title.split('/')[0]}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const LengthDropdown = ({
  lengthTags,
  selectedLengthTag,
  setSelectedLengthTag,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownRef]);

  const handleClick = (tag) => {
    setSelectedLengthTag(tag);
    setIsOpen(false);
  };

  return (
    <div className="w-full relative mb-[8px]" ref={dropdownRef}>
      <div
        className="flex flex-row justify-between pl-[10px] pr-[10px] w-full border-1 border-grey-400 rounded-[3px] text-h6 text-grey-600 items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-grey-600 text-h6 py-[6px]">
          {selectedLengthTag.replace('length::', 'Length: ')}
        </span>
        <Icon id="chevron-down" className="h-[10px] w-[10px]" />
      </div>
      <div
        className={`max-h-[200px] overflow-y-scroll bg-white border-1 border-grey-400 rounded-b-[3px] border-t-0 absolute w-full z-1 ${
          isOpen ? 'block' : 'hidden'
        }`}
      >
        {lengthTags.map((tag) => (
          <div
            onClick={() => handleClick(tag)}
            className="pl-[10px] cursor-pointer flex flex-row items-center pt-[9px] pb-[9px] hover:bg-tan-100"
          >
            <p className="text-h6 text-grey-600">{tag.split('::')[1]}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const AddToCartButton = ({ variant }) => {
  const handleAddToCart = () => {
    const payload = {
      id: variant.node.id.split('gid://shopify/ProductVariant/')[1],
      quantity: 1,
    };

    document.dispatchEvent(
      new CustomEvent('cart:add', {
        detail: {
          payload,
        },
      }),
    );
  };

  return (
    <>
      {variant && (
        <div className="flex items-center h-[50px]" id="addtobag">
          <button
            id="atc-button"
            className={`${
              !variant.node.available
                ? 'bg-grey-300 border border-1 border-grey-400 text-grey-700'
                : 'bg-text-1 text-white'
            } grow self-stretch rounded-[6px] cursor-pointer`}
            onClick={() => handleAddToCart()}
            disabled={!variant.node.available}
          >
            {variant.node.available
              ? window.TMW.Strings.product.addToCart
              : window.TMW.Strings.product.unavailable}
          </button>
        </div>
      )}
    </>
  );
};

export const PairItWith = () => {
  const { data } = useProduct();
  if (data.pairItWith === undefined) return;

  const [products, setProducts] = useState(null);
  const [variants, setVariants] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [lengthTags, setLengthTags] = useState(null);
  const [selectedLengthTag, setSelectedLengthTag] = useState(null);
  const storefront = new Storefront();
  const sectionTitle = data.pairItWith[0].title;

  useEffect(() => {
    const fetchProducts = async () => {
      const sku = data.product.variants[0].sku;
      const ssProfile = data.pairItWith[0].profile;
      const ssSiteID = data.pairItWith[0].siteid;

      const ssBaseUrl = `https://${ssSiteID}.a.searchspring.io/boost/${ssSiteID}/recommend?tags=${ssProfile}&limits=1&product=${sku}&siteId=${ssSiteID}`;
      const ssResponse = await fetch(ssBaseUrl);
      const ssData = await ssResponse.json();

      if (!ssData) {
        return;
      }

      const handleResponseJson = await storefront.request({
        query: productByHandle(),
        variables: { handle: ssData[0].results[0]?.attributes.handle },
      });
      const productData = handleResponseJson.data?.productByHandle;
      const groupTag = productData?.tags.find((tag) => tag.includes('group::'));

      if (!groupTag) {
        return;
      }

      const responseJson = await storefront.request({
        query: productsByTag(groupTag),
      });
      const filteredResponseJson = responseJson.data?.products.nodes.filter(
        (t) => t != null,
      );

      const tagsWithLength = [];

      filteredResponseJson.forEach((product) => {
        const tag = product.tags.find((tag) => tag.includes('length::'));
        product.lengthTag = tag;
        if (tag) {
          tagsWithLength.push(tag);
        }
      });

      const uniqueTagsWithLength = [...new Set(tagsWithLength)];
      setLengthTags(uniqueTagsWithLength);
      setSelectedLengthTag(filteredResponseJson[0].lengthTag);

      setProducts(filteredResponseJson);
      setSelectedProduct(filteredResponseJson[0]);
      setVariants(filteredResponseJson[0].variants.edges);
      setSelectedVariant(filteredResponseJson[0].variants.edges[0]);
    };

    fetchProducts();
  }, []);

  if (!products) {
    return null;
  }

  return (
    <div id="pair-it-with">
      <h3 className="my-[20px]">{sectionTitle}</h3>
      <div className="flex gap-5 px-5 py-6 pt-6 rounded-md border-1 border-grey-400">
        <div className="max-w-[152px] md:max-w-[300px] lg:max-w-[198px]">
          <a href={selectedProduct.handle}>
            <Image
              src={selectedVariant.node.image.url}
              alt={selectedVariant.node.image.alt}
              srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
              additionalClasses="w-full"
            />
          </a>
        </div>
        <div className="w-full">
          <div>
            <a href={selectedProduct.handle}>
              <h4>{selectedProduct.title.split(':')[0]}</h4>
            </a>
            <div className="flex items-center gap-[10px] mb-[10px]">
              <p>
                {formatMoney({ amount: selectedVariant.node.price.amount })}
              </p>
              <Reviews activeProduct={selectedProduct} />
            </div>
          </div>
          <ProductDropdown
            products={products}
            selectedProduct={selectedProduct}
            selectedVariant={selectedVariant}
            setSelectedProduct={setSelectedProduct}
            setSelectedVariant={setSelectedVariant}
            selectedLengthTag={selectedLengthTag}
          />
          <VariantDropdown
            variants={variants}
            products={products}
            selectedVariant={selectedVariant}
            selectedProduct={selectedProduct}
            setSelectedVariant={setSelectedVariant}
          />
          {selectedLengthTag && (
            <LengthDropdown
              lengthTags={lengthTags}
              selectedLengthTag={selectedLengthTag}
              setSelectedLengthTag={setSelectedLengthTag}
            />
          )}
          <AddToCartButton variant={selectedVariant} />
        </div>
      </div>
    </div>
  );
};

export default PairItWith;
